'use client';

import { useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { useAuthStore } from '@/stores/auth.store';

// Dashboard hidden for now — redirecting admins to their role landing page.
// To restore: remove the redirect() export, uncomment everything below,
// and restore the original export default DashboardPage at the bottom.

export default function AdminRootPage() {
  const router = useRouter();
  const admin = useAuthStore(s => s.admin);

  useEffect(() => {
    if (!admin) return;

    if (admin.role === 'super_admin') {
      router.replace('/admin/series');
      return;
    }

    if (admin.role === 'content_manager' || admin.role === 'content_approver') {
      router.replace('/admin/series');
      return;
    }

    router.replace('/admin/settings');
  }, [admin, router]);

  return (
    <div className="min-h-[40vh] flex items-center justify-center text-xs text-white/40">
      Redirecting…
    </div>
  );
}

// ── Dashboard (commented out) ─────────────────────────────────────────────────

// 'use client';
// import React, { useState } from 'react';
// import { MetricCard, PageHeader, Card, Btn, StatusBadge, useToast, ToastProvider } from './components/shared';
// import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
//
// const userActivity = [
//   { day: 'Mon', dau: 16200, new: 2100 },
//   { day: 'Tue', dau: 17400, new: 2400 },
//   { day: 'Wed', dau: 16800, new: 1900 },
//   { day: 'Thu', dau: 18100, new: 2700 },
//   { day: 'Fri', dau: 18900, new: 3100 },
//   { day: 'Sat', dau: 19200, new: 3400 },
//   { day: 'Sun', dau: 18240, new: 2840 },
// ];
//
// const topSeries = [
//   { title: 'Broken Vows',   genre: 'Romance', views: '2.4M', completion: '82%', status: 'Published' },
//   { title: 'The Last Heir', genre: 'Thriller', views: '1.9M', completion: '76%', status: 'Published' },
//   { title: 'Midnight CEO',  genre: 'Drama',    views: '1.6M', completion: '71%', status: 'Published' },
//   { title: 'Forbidden Love',genre: 'Romance',  views: '1.3M', completion: '68%', status: 'Published' },
//   { title: 'Dark Secrets',  genre: 'Mystery',  views: '1.1M', completion: '65%', status: 'Scheduled' },
// ];
//
// const alerts = [
//   { type: 'error' as const,   msg: '3 payment-success but Keys-missing events in last hour',          time: '2m ago' },
//   { type: 'warning' as const, msg: '146 playback errors detected — BunnyNet CDN latency spike',       time: '8m ago' },
//   { type: 'warning' as const, msg: 'Analytics data is 18 minutes stale — PostHog pipeline delay',    time: '18m ago' },
//   { type: 'info' as const,    msg: '7 videos stuck in transcoding queue — BunnyNet pipeline review', time: '45m ago' },
// ];
//
// const CustomTooltip = ({ active, payload, label }: { active?: boolean; payload?: { value: number; name: string }[]; label?: string }) => {
//   if (active && payload && payload.length) {
//     return (
//       <div className="bg-[#1A1A1A] border border-white/10 rounded-brand-md px-3 py-2 text-xs">
//         <div className="text-white/40 mb-1">{label}</div>
//         {payload.map((p, i) => (
//           <div key={i} className="text-white/80">{p.name}: <span className="text-[#C3FB15] font-medium">{p.value.toLocaleString()}</span></div>
//         ))}
//       </div>
//     );
//   }
//   return null;
// };
//
// function DashboardPageInner() {
//   const { showToast } = useToast();
//   const [range, setRange] = useState('Last 7 days');
//
//   return (
//     <div className="p-6 max-w-[1600px] mx-auto">
//       <PageHeader
//         title="Dashboard"
//         subtitle="WalkTalk Studios — operations overview"
//         actions={
//           <div className="flex items-center gap-2">
//             <div className="flex items-center gap-1 bg-white/5 border border-white/8 rounded-brand-md p-0.5">
//               {['Today', 'Last 7 days', 'Last 30 days'].map((r) => (
//                 <button
//                   key={r}
//                   onClick={() => setRange(r)}
//                   className={`px-3 py-1.5 rounded-[10px] text-xs font-medium transition-all ${range === r ? 'bg-[#C3FB15] text-[#0A0A0A]' : 'text-white/40 hover:text-white/80'}`}
//                 >
//                   {r}
//                 </button>
//               ))}
//             </div>
//             <Btn variant="secondary" size="sm" onClick={() => showToast('Exporting dashboard data…', 'info')}>↓ Export</Btn>
//           </div>
//         }
//       />
//
//       {/* Alert strip */}
//       <div className="space-y-2 mb-6">
//         {alerts.map((a, i) => (
//           <div key={i} className={`flex items-center justify-between px-4 py-2.5 rounded-brand-md border text-sm ${
//             a.type === 'error'   ? 'bg-red-500/10 border-red-500/30 text-red-400' :
//             a.type === 'warning' ? 'bg-amber-500/10 border-amber-500/30 text-amber-400' :
//                                    'bg-white/5 border-white/10 text-white/60'
//           }`}>
//             <div className="flex items-center gap-2">
//               <span>{a.type === 'error' ? '✕' : a.type === 'warning' ? '⚠' : 'ℹ'}</span>
//               <span>{a.msg}</span>
//             </div>
//             <span className="text-xs opacity-60 flex-shrink-0 ml-4">{a.time}</span>
//           </div>
//         ))}
//       </div>
//
//       {/* Core metrics */}
//       <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
//         <MetricCard label="Total Users"           value="128,450" change="2.1% vs yesterday"  positive icon="◉" />
//         <MetricCard label="DAU"                   value="18,240"  change="4.3% vs last week"   positive icon="◎" />
//         <MetricCard label="New Signups"            value="2,840"   sub="Today" change="12% vs yesterday" positive icon="+" />
//         <MetricCard label="Revenue Today"          value="₹8.7L"   change="18% vs yesterday"  positive icon="₹" />
//         <MetricCard label="Active Subscriptions"   value="11,200"  change="1.4% vs yesterday" positive icon="◌" />
//         <MetricCard label="Payment Success"        value="96.4%"   change="0.3% vs yesterday" positive icon="✓" />
//       </div>
//
//       {/* Chart + Ops health */}
//       <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
//         <Card className="lg:col-span-2 p-5">
//           <div className="flex items-center justify-between mb-4">
//             <div>
//               <div className="text-sm font-medium">User Activity</div>
//               <div className="text-xs text-white/40">DAU &amp; New Signups — {range}</div>
//             </div>
//             <div className="flex items-center gap-3 text-xs text-white/40">
//               <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#C3FB15] inline-block" />DAU</span>
//               <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-white/40 inline-block" />New Signups</span>
//             </div>
//           </div>
//           <ResponsiveContainer width="100%" height={200}>
//             <BarChart data={userActivity} barGap={4}>
//               <XAxis dataKey="day" tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 11 }} axisLine={false} tickLine={false} />
//               <YAxis hide />
//               <Tooltip content={<CustomTooltip />} />
//               <Bar dataKey="dau" name="DAU"         fill="#C3FB15"              radius={[3,3,0,0]} opacity={0.85} />
//               <Bar dataKey="new" name="New Signups" fill="rgba(255,255,255,0.3)" radius={[3,3,0,0]} opacity={0.85} />
//             </BarChart>
//           </ResponsiveContainer>
//         </Card>
//
//         <Card className="p-5">
//           <div className="text-sm font-medium mb-4">Operations Health</div>
//           <div className="space-y-3">
//             {[
//               { label: 'Ad Completion Rate',    value: '78%', color: 'bg-[#C3FB15]', pct: 78 },
//               { label: 'Playback Errors',        value: '146', color: 'bg-red-500',   pct: 15 },
//               { label: 'Videos Transcoding',     value: '7',   color: 'bg-amber-500', pct: 10 },
//               { label: 'Open Support Issues',    value: '54',  color: 'bg-blue-500',  pct: 20 },
//               { label: 'Failed Notifications',   value: '12',  color: 'bg-white/30',  pct: 5  },
//             ].map((item, i) => (
//               <div key={i}>
//                 <div className="flex items-center justify-between text-xs mb-1">
//                   <span className="text-white/40">{item.label}</span>
//                   <span className="font-medium text-white/80">{item.value}</span>
//                 </div>
//                 <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
//                   <div className={`h-full ${item.color} rounded-full`} style={{ width: `${item.pct}%` }} />
//                 </div>
//               </div>
//             ))}
//           </div>
//           <div className="mt-5 pt-4 border-t border-white/8 space-y-2">
//             <div className="text-xs text-white/30 font-medium uppercase tracking-wider mb-2">Payment Exceptions</div>
//             {[
//               { label: 'Keys missing after payment', count: 3, color: 'text-red-400'   },
//               { label: 'Entitlement failures',       count: 7, color: 'text-amber-400' },
//               { label: 'Duplicate webhooks',         count: 2, color: 'text-white/50'  },
//             ].map((item, i) => (
//               <div key={i} className="flex items-center justify-between text-xs">
//                 <span className="text-white/40">{item.label}</span>
//                 <span className={`font-semibold ${item.color}`}>{item.count}</span>
//               </div>
//             ))}
//           </div>
//         </Card>
//       </div>
//
//       {/* Top Series */}
//       <Card className="p-5">
//         <div className="flex items-center justify-between mb-4">
//           <div className="text-sm font-medium">Top Performing Series</div>
//           <Btn variant="ghost" size="sm" onClick={() => showToast('Loading full series list…', 'info')}>View all →</Btn>
//         </div>
//         <div className="space-y-3">
//           {topSeries.map((s, i) => (
//             <div key={i} className="flex items-center gap-3">
//               <div className="w-6 h-6 rounded-brand-sm bg-[#C3FB15]/10 flex items-center justify-center text-xs font-bold text-[#C3FB15] flex-shrink-0">
//                 {i + 1}
//               </div>
//               <div className="flex-1 min-w-0">
//                 <div className="text-sm font-medium truncate">{s.title}</div>
//                 <div className="text-xs text-white/40">{s.genre}</div>
//               </div>
//               <div className="text-right flex-shrink-0">
//                 <div className="text-sm font-medium">{s.views}</div>
//                 <div className="text-xs text-white/40">{s.completion} completion</div>
//               </div>
//               <StatusBadge status={s.status} />
//             </div>
//           ))}
//         </div>
//       </Card>
//     </div>
//   );
// }
//
// export default function DashboardPage() {
//   return (
//     <ToastProvider>
//       <DashboardPageInner />
//     </ToastProvider>
//   );
// }
